import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { 
  Shield, Phone, UserX, CheckCircle, ArrowRight,
  EyeOff, X, MessageSquare
} from 'lucide-react';
import { trackCTAClick } from '@/utils/analytics';

// AEO Schema
const seoSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Send Messages Without Sharing Your Phone Number | Privluxe",
  "description": "Learn how to send messages to someone without giving them your phone number. Privluxe lets you communicate privately without revealing your contact details.",
  "mainEntity": {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Can I message someone without giving them my phone number?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. With Privluxe, you can send messages to anyone without sharing your phone number. You simply send an invite link, and once they accept, you can chat privately. Your number stays hidden."
        }
      },
      {
        "@type": "Question",
        "name": "How do I send a message without revealing my number?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sign up for Privluxe, create an anonymous invite link, and share it with the person you want to message. They'll see your username only—not your phone number or email."
        }
      },
      {
        "@type": "Question",
        "name": "Why would I want to message without sharing my number?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "There are many reasons: meeting new people online, selling items, business contacts you don't fully trust, or simply wanting to keep your personal number private until you're ready to share it."
        }
      }
    ]
  }
};

export default function SendMessageWithoutPhone() {
  const navigate = useNavigate();

  const handleCTAClick = (location) => {
    trackCTAClick(location, 'Message Without Phone');
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>Send Messages Without Sharing Your Phone Number | Privluxe</title>
        <meta name="description" content="Learn how to send messages to someone without giving them your phone number. Privluxe lets you communicate privately without revealing your contact details." />
        <meta name="keywords" content="message without phone number, text without giving number, private messaging, anonymous texting, hide phone number, chat without sharing number" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://privluxe.com/send-message-without-phone-number" />
        <script type="application/ld+json">
          {JSON.stringify(seoSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen" style={{ background: '#020617' }}>
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(2, 6, 23, 0.9)', backdropFilter: 'blur(20px)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <a href="/" className="flex items-center gap-2">
                <Shield className="h-6 w-6" style={{ color: '#7C3AED' }} />
                <span className="text-lg font-bold tracking-[0.2em]" style={{ color: '#E5E7EB' }}>PRIVLUXE</span>
              </a>
              <Button 
                onClick={() => handleCTAClick('navbar_cta')}
                className="text-white text-sm px-6"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              >
                Start Chatting
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4">
          <div 
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)' }}
          />
          
          <div className="relative z-10 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(124, 58, 237, 0.2)', border: '1px solid rgba(124, 58, 237, 0.3)' }}>
              <Phone className="h-4 w-4" style={{ color: '#7C3AED' }} />
              <span className="text-sm" style={{ color: '#E5E7EB' }}>Keep Your Number Private</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight" style={{ color: '#E5E7EB' }}>
              How to Send Messages Without<br />
              <span style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Sharing Your Phone Number
              </span>
            </h1>

            <p className="text-lg mb-8 max-w-3xl" style={{ color: 'rgba(229, 231, 235, 0.85)' }}>
              <strong style={{ color: '#22D3EE' }}>Yes, you can.</strong> With Privluxe, you can send messages to anyone without giving them your phone number. Your contact details stay completely private.
            </p>

            <p className="text-base mb-10 max-w-3xl" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              Unlike WhatsApp or Telegram where your phone number is visible to everyone you chat with, Privluxe keeps your number hidden. You communicate through anonymous invite links instead.
            </p>

            <div className="flex flex-col items-start gap-2">
              <Button 
                onClick={() => handleCTAClick('hero_cta')}
                className="h-14 px-8 text-white text-lg font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                data-testid="no-phone-cta"
              >
                Start Private Chat
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>No cost. No identity exposure.</span>
            </div>
          </div>
        </section>

        {/* The Problem Section */}
        <section className="py-16 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#E5E7EB' }}>
              The Problem with Traditional Messaging Apps
            </h2>
            
            <p className="text-base mb-8" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              On most messaging apps, your phone number is tied to your identity. When you message someone new, they immediately have access to your personal number.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,100,100,0.1)', border: '1px solid rgba(255,100,100,0.2)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <X className="h-6 w-6" style={{ color: '#FF6B6B' }} />
                  <h3 className="font-semibold" style={{ color: '#E5E7EB' }}>WhatsApp</h3>
                </div>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Everyone you message can see your phone number. No way around it.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,100,100,0.1)', border: '1px solid rgba(255,100,100,0.2)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <X className="h-6 w-6" style={{ color: '#FF6B6B' }} />
                  <h3 className="font-semibold" style={{ color: '#E5E7EB' }}>Telegram</h3>
                </div>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Requires phone number for account. Others can find you by number.</p>
              </div>

              <div className="p-6 rounded-xl md:col-span-2" style={{ background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.2)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="h-6 w-6" style={{ color: '#22D3EE' }} />
                  <h3 className="font-semibold" style={{ color: '#E5E7EB' }}>Privluxe</h3>
                </div>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Your phone number is never shared with the people you chat with. They see only your username. You control what personal information you share.</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E5E7EB' }}>
              How to Message Without Your Phone Number
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>1</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Sign Up for Privluxe</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Create an account with your email or phone. This is for your account security only—it's never shared with anyone you chat with.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>2</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Create an Invite Link</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Generate an anonymous invite link. This link doesn't contain your phone number or email—just a unique code that connects to your account.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>3</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Share It Anywhere</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Send the link via SMS, email, social media, or any app. You can even write it on paper. The link is the only thing shared—not your contact info.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>4</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Chat Privately</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Once they accept your invite, you can message freely. They'll see your username only. Your phone number stays completely hidden.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E5E7EB' }}>
              When You Might Want to Hide Your Number
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <CheckCircle className="h-6 w-6 mb-3" style={{ color: '#22D3EE' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Meeting people online</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Chat first without giving out your real number to strangers.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <CheckCircle className="h-6 w-6 mb-3" style={{ color: '#22D3EE' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Selling items online</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Communicate with buyers without exposing your personal number.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <CheckCircle className="h-6 w-6 mb-3" style={{ color: '#22D3EE' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Business contacts</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Keep professional and personal numbers separate.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <CheckCircle className="h-6 w-6 mb-3" style={{ color: '#22D3EE' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>General privacy</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Simply not wanting everyone to have access to your number.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E5E7EB' }}>
              Questions About Private Messaging
            </h2>

            <div className="space-y-6">
              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Do I need a phone number to sign up?</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>You can sign up with either an email or phone number. Whichever you choose is used only for your account—it's never visible to people you chat with.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Can I share my number later if I want to?</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Yes. You're always in control. If you decide you trust someone and want to share your number, you can simply type it in your conversation.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Is this really different from using a fake number?</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Yes. Fake number apps still require you to share some number. Privluxe doesn't use phone numbers for communication at all—just secure invite links and usernames.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 text-center" style={{ background: 'rgba(124, 58, 237, 0.1)' }}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#E5E7EB' }}>
              Ready to Chat Without Sharing Your Number?
            </h2>
            <p className="text-lg mb-8" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              Start your first private conversation. Your phone number stays hidden.
            </p>
            <div className="flex flex-col items-center gap-2">
              <Button 
                onClick={() => handleCTAClick('final_cta')}
                className="h-14 px-10 text-white text-lg font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              >
                Start Private Chat
              </Button>
              <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>No cost. No identity exposure.</span>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
